/* LUCRÜ™ — © 2026 LUCRÜ. All rights reserved. */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { XIcon, KeyIcon, SlidersIcon, ShieldIcon, UserMinusIcon, SparkleIcon, SpeakerHighIcon } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import VoiceCloneUpload from "@/components/VoiceCloneUpload";

const SettingsPanel = ({ settings, sessionId, onClose, onSave }) => {
  const [local, setLocal] = useState(settings);
  const [voices, setVoices] = useState([]);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [bannedInput, setBannedInput] = useState((settings.banned_words || []).join(", "));
  const [ignoredInput, setIgnoredInput] = useState((settings.ignored_users || []).join(", "));

  useEffect(() => {
    if (local.elevenlabs_api_key) fetchVoices(local.elevenlabs_api_key);
    // eslint-disable-next-line
  }, []);

  const fetchVoices = async (key) => {
    if (!key) return;
    setLoadingVoices(true);
    try {
      const r = await api.post("/tts/voices", { api_key: key });
      setVoices(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not fetch voices");
    } finally {
      setLoadingVoices(false);
    }
  };

  const update = (k, v) => setLocal((p) => ({ ...p, [k]: v }));

  const save = async () => {
    const payload = {
      ...local,
      banned_words: bannedInput.split(",").map((w) => w.trim()).filter(Boolean),
      ignored_users: ignoredInput.split(",").map((w) => w.trim().toLowerCase()).filter(Boolean),
    };
    await onSave(payload);
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-stretch justify-end"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="w-full max-w-xl bg-[#0F0F16] border-l border-[#222233] overflow-y-auto"
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 25 }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sticky top-0 bg-[#0F0F16] border-b border-[#222233] px-6 py-4 flex items-center justify-between">
          <div className="font-display font-black text-lg">Settings</div>
          <button onClick={onClose} data-testid="close-settings" className="p-2 hover:text-[#00E5FF]">
            <XIcon size={18} />
          </button>
        </header>

        <div className="p-6 space-y-6">
          {/* API key */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <KeyIcon size={16} color="#00E5FF" />
              <h3 className="font-display font-black text-sm uppercase tracking-widest">ElevenLabs API Key</h3>
            </div>
            <input
              type="password"
              value={local.elevenlabs_api_key || ""}
              onChange={(e) => update("elevenlabs_api_key", e.target.value)}
              onBlur={(e) => fetchVoices(e.target.value)}
              placeholder="sk_..."
              data-testid="api-key-input"
              className="w-full bg-[#05050A] border border-[#222233] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#00E5FF]"
            />
            <p className="text-[11px] text-white/40 mt-2">
              Get your key at <a className="text-[#00E5FF]" href="https://elevenlabs.io/app/settings/api-keys" target="_blank" rel="noreferrer">elevenlabs.io</a>. Fees are billed on your ElevenLabs account.
            </p>
          </section>

          {/* Voice */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <SpeakerHighIcon size={16} color="#39FF14" />
              <h3 className="font-display font-black text-sm uppercase tracking-widest">Voice</h3>
              {loadingVoices && <span className="text-xs text-white/40">loading…</span>}
            </div>
            <select
              value={local.voice_id || ""}
              onChange={(e) => {
                const v = voices.find((x) => x.voice_id === e.target.value);
                update("voice_id", e.target.value);
                update("voice_name", v?.name || "");
              }}
              data-testid="voice-select"
              className="w-full bg-[#05050A] border border-[#222233] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#00E5FF]"
            >
              <option value="">— Pick a voice —</option>
              {voices.map((v) => (
                <option key={v.voice_id} value={v.voice_id}>
                  {v.name} {v.category ? `· ${v.category}` : ""}
                </option>
              ))}
            </select>

            <div className="grid grid-cols-2 gap-3 mt-4">
              <div>
                <label className="text-[11px] text-white/50 uppercase tracking-wider">Stability</label>
                <input
                  type="range" min="0" max="1" step="0.05"
                  value={local.stability}
                  onChange={(e) => update("stability", parseFloat(e.target.value))}
                  data-testid="stability-slider"
                  className="w-full accent-[#00E5FF]"
                />
                <div className="text-xs text-white/40">{local.stability.toFixed(2)}</div>
              </div>
              <div>
                <label className="text-[11px] text-white/50 uppercase tracking-wider">Similarity</label>
                <input
                  type="range" min="0" max="1" step="0.05"
                  value={local.similarity_boost}
                  onChange={(e) => update("similarity_boost", parseFloat(e.target.value))}
                  data-testid="similarity-slider"
                  className="w-full accent-[#00E5FF]"
                />
                <div className="text-xs text-white/40">{local.similarity_boost.toFixed(2)}</div>
              </div>
            </div>

            <div className="mt-4">
              <VoiceCloneUpload
                sessionId={sessionId}
                settings={local}
                onUpdate={(patch) => setLocal((p) => ({ ...p, ...patch }))}
              />
            </div>
          </section>

          {/* Smart mode */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <SparkleIcon size={16} color="#FF007F" />
              <h3 className="font-display font-black text-sm uppercase tracking-widest">AI Behaviour</h3>
            </div>
            <label className="flex items-center justify-between bg-[#05050A] border border-[#222233] rounded-lg px-3 py-3 cursor-pointer">
              <div>
                <div className="text-sm font-semibold">Smart comment mode</div>
                <div className="text-xs text-white/50">Reads + adds a witty one-liner via Claude</div>
              </div>
              <input
                type="checkbox"
                checked={!!local.smart_mode}
                onChange={(e) => update("smart_mode", e.target.checked)}
                data-testid="smart-mode-toggle"
                className="w-5 h-5 accent-[#00E5FF]"
              />
            </label>
            <label className="flex items-center justify-between bg-[#05050A] border border-[#222233] rounded-lg px-3 py-3 mt-2 cursor-pointer">
              <div>
                <div className="text-sm font-semibold">Read username</div>
                <div className="text-xs text-white/50">Say the chatter name before the message</div>
              </div>
              <input
                type="checkbox"
                checked={!!local.read_username}
                onChange={(e) => update("read_username", e.target.checked)}
                data-testid="read-username-toggle"
                className="w-5 h-5 accent-[#00E5FF]"
              />
            </label>
            <label className="flex items-center justify-between bg-[#05050A] border border-[#222233] rounded-lg px-3 py-3 mt-2 cursor-pointer">
              <div>
                <div className="text-sm font-semibold">Record session (audio)</div>
                <div className="text-xs text-white/50">Store Lyra's TTS clips so you can download the full zip (chat + mp3s)</div>
              </div>
              <input
                type="checkbox"
                checked={!!local.record_session}
                onChange={(e) => update("record_session", e.target.checked)}
                data-testid="record-session-toggle"
                className="w-5 h-5 accent-[#39FF14]"
              />
            </label>
          </section>

          {/* Moderation */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <ShieldIcon size={16} color="#00E5FF" />
              <h3 className="font-display font-black text-sm uppercase tracking-widest">Moderation</h3>
            </div>
            <label className="text-[11px] text-white/50 uppercase tracking-wider">Banned words (comma-separated)</label>
            <input
              value={bannedInput}
              onChange={(e) => setBannedInput(e.target.value)}
              placeholder="spam, link, discord"
              data-testid="banned-words-input"
              className="w-full bg-[#05050A] border border-[#222233] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#00E5FF] mt-1"
            />
            <label className="text-[11px] text-white/50 uppercase tracking-wider mt-3 block">Ignored users</label>
            <input
              value={ignoredInput}
              onChange={(e) => setIgnoredInput(e.target.value)}
              placeholder="nightbot, streamelements"
              data-testid="ignored-users-input"
              className="w-full bg-[#05050A] border border-[#222233] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#00E5FF] mt-1"
            />

            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className="text-[11px] text-white/50 uppercase tracking-wider">Min length</label>
                <input
                  type="number"
                  value={local.min_message_length}
                  onChange={(e) => update("min_message_length", parseInt(e.target.value || "0"))}
                  data-testid="min-length-input"
                  className="w-full bg-[#05050A] border border-[#222233] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#00E5FF] mt-1"
                />
              </div>
              <div>
                <label className="text-[11px] text-white/50 uppercase tracking-wider">Max length</label>
                <input
                  type="number"
                  value={local.max_message_length}
                  onChange={(e) => update("max_message_length", parseInt(e.target.value || "0"))}
                  data-testid="max-length-input"
                  className="w-full bg-[#05050A] border border-[#222233] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#00E5FF] mt-1"
                />
              </div>
            </div>
          </section>

          <div className="pt-2 flex gap-3">
            <button onClick={save} className="btn-pill btn-primary flex-1 justify-center" data-testid="save-settings-button">
              Save settings
            </button>
            <button onClick={onClose} className="btn-pill btn-ghost">Close</button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default SettingsPanel;
